import { useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import { ButtonBase } from '../../../../shared/Button';

import {
  setHasError,
  unsetHasError,
} from '../../../../lib/redux/campaignPrize/campaignPrizeSlice';

import { useUpdatePostMutation } from './hooks/usePostMutation';

import { ERROR_TIMER, LIVE, SCHEDULED } from '../../utils/constants';
import { dateHasPassed } from '../../utils/helpers';
import Tooltip from '../../../../shared/Tooltip/Tooltip.jsx';
import { useNotification } from '../../../../shared/Notification/useNotification';

const ApprovedButton = ({ setOpenCancelModal, message, status }) => {
  const { t } = useTranslation('posts');

  if (status === LIVE && !!message) return null;

  return (
    <Container>
      <CancelBtn
        variant="black"
        mode="outline"
        onClick={() => setOpenCancelModal(true)}
      >
        {t('postNotifications.revertToDraft')}
      </CancelBtn>
    </Container>
  );
};

const NotApprovedButton = ({
  post,
  tiles,
  campaignId,
  setOpenScheduleModal,
  setShowIncompleteBorder,
  setNoDateError,
}) => {
  const { t } = useTranslation('posts');
  const dispatch = useDispatch();
  const { mutate } = useUpdatePostMutation();
  const hasError = useSelector(state => state.campaignPrize.hasError);
  const { showError } = useNotification();

  const timezone = useSelector(state => state.currentBoard.timezone);

  const { prize, publishDate, sendTime } = post;
  const hasIncompleteTile = tiles.some(tile => !tile.fullyAssembled);

  const hasPassed = dateHasPassed(publishDate, sendTime, timezone);

  const validPrize = useCallback(() => {
    if (prize.winners.length) return true;

    return false;
  }, [prize]);

  useEffect(() => {
    if (!prize) return undefined;
    if (prize && validPrize()) return undefined;

    return mutate({ id: post.uuid, campaignId });
  }, [campaignId, mutate, post.uuid, prize, validPrize]);

  useEffect(() => {
    if (!hasError) return undefined;
    const timer = setTimeout(() => {
      dispatch(unsetHasError());
    }, ERROR_TIMER);
    return () => clearTimeout(timer);
  }, [dispatch, hasError]);

  const handlePrizeClick = () => {
    if (validPrize()) {
      setOpenScheduleModal(true);
    } else {
      dispatch(setHasError());
    }
  };

  const onSchedule = () => {
    if (prize) {
      handlePrizeClick();
    } else {
      setOpenScheduleModal(true);
    }
  };

  const onSubmit = () => {
    if (!publishDate) return setNoDateError(true);
    if (hasIncompleteTile) {
      showError({
        title: t('postNotifications.incompleteTilesNotificationTitle'),
        content: t('postNotifications.incompleteTilesNotificationContent'),
      });
      return setShowIncompleteBorder(true);
    }

    return onSchedule();
  };

  return (
    <Container>
      <Tooltip />
      <ScheduleButton
        data-testid="schedule-btn"
        mode="contained"
        variant="primary"
        onClick={onSubmit}
        disabled={hasPassed}
        data-tip={hasPassed ? t('postNotifications.pastDateError') : ''}
      >
        {post.message ? (
          'Review and Schedule'
        ) : (
          <>
            <i className="fa-regular fa-bell"></i>
            {t('postNotifications.scheduleTiles')}
          </>
        )}
      </ScheduleButton>
    </Container>
  );
};

export const ScheduleButtonContainer = ({
  post,
  tiles,
  campaignId,
  setOpenScheduleModal,
  setOpenCancelModal,
  setShowIncompleteBorder,
  setNoDateError,
  setMakeLive,
}) => {
  const approved = post.status === LIVE || post.status === SCHEDULED;

  return (
    <>
      {approved ? (
        <ApprovedButton
          status={post.status}
          setOpenCancelModal={setOpenCancelModal}
          message={post.message}
        />
      ) : (
        <NotApprovedButton
          post={post}
          tiles={tiles}
          campaignId={campaignId}
          setOpenScheduleModal={setOpenScheduleModal}
          setShowIncompleteBorder={setShowIncompleteBorder}
          setNoDateError={setNoDateError}
          setMakeLive={setMakeLive}
        />
      )}
    </>
  );
};

const Container = styled.div`
  display: flex;
  margin: 0px 20px;
  position: relative;
`;

const CancelBtn = styled(ButtonBase)`
  width: 100%;
  display: flex;
  height: 40px;
  padding: 8px 12px;
  align-items: center;
  justify-content: center;
`;

const ScheduleButton = styled(ButtonBase)`
  display: flex;
  width: 100%;
  justify-content: center;
  align-items: center;
  padding: 8px 12px;
  height: 40px;
  margin-right: 1px;
  border-radius: 8px;
  gap: 12px;
`;
